import { LogManager } from '@src/utility/Logger';
import { makeAutoObservable, observable, runInAction } from 'mobx';

import type { RootStore } from './RootStore';
import type {
  DubGenerateResult,
  GenerateDubParams,
  SeparateStemsParams,
  StemSeparateResult,
} from '@src/repositories/DubbingRepository';
import type { SpeakerAiVoiceMapping } from '@src/repositories/SpeakerRepository';
import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';

export type DubbingCatalogLoadingStatus =
  | 'idle'
  | 'loading'
  | 'loaded'
  | 'error';

/**
 * The one dub-generate request in flight (PLAT-11570). Owned by the store, not
 * the popover, so a person can close the popover and keep working: the request
 * outlives the unmount, every language toggle can show it running, and any of
 * them can cancel it. Replaced wholesale (observable.ref) and cleared when the
 * request settles, whichever way.
 */
export interface DubRun {
  storyId: string;
  targetLanguage: string;
  /** "Italian (Italy)" — for the on-page chip and the completion toast. */
  languageCaption: string;
  /** Epoch ms the request started; anchors the time-estimated progress so a
   *  remount (reopening the popover, another toggle) continues rather than
   *  restarting from 0%. */
  startedAt: number;
  /** Expected wall-clock for this run (estimateDubGenerateMs), computed once so
   *  the popover and the chip interpolate the same curve. */
  etaMs: number;
  /** Started by the automatic re-dub on commit, not by a person. */
  automatic?: boolean;
}

// Everything the repository sends, minus the cancel handle the store owns. So a
// field added to the request (`direction`, PLAT-11384) reaches the wire from
// every caller of `startRun` with no change here — the store forwards it as is.
export type StartDubRunParams = Omit<GenerateDubParams, 'signal'>;
export type DubRunMeta = Pick<
  DubRun,
  'languageCaption' | 'etaMs' | 'automatic'
>;

/**
 * The voice/music separation in flight. Held here for the reason `DubRun` is:
 * the split takes minutes and is usually started from the dub popover's "Keep
 * Original Music" box, so it must outlive the surface that asked for it — and
 * the on-page chip beside the dub's has to be able to see it.
 */
export interface SeparationRun {
  storyId: string;
  /** Epoch ms the request started; anchors the estimated progress. */
  startedAt: number;
}

export type StartSeparationParams = Omit<SeparateStemsParams, 'signal'>;

// Store-scoped catalog of active AI Voices — the pickable pool for workspace
// activation (Settings → AI Voices) and the later "assign-new" pool for the
// speaker mapper. Eagerly hydrated at workspace init and re-hydrated on
// workspace switch — no lazy/cold fetch, no module-level cache. Twin of
// TranslationHandlerStore / LanguageStore. The showcase's *saved* activation
// (dubbingEnabled + activatedAiVoices) rides on `organization`, mirroring
// translation; this store holds the catalog those selections are drawn from.
export class DubbingStore {
  catalog: AiVoiceRecord[] = [];
  loadingStatus: DubbingCatalogLoadingStatus = 'idle';
  // The showcase the catalog is hydrated for; a different id re-hydrates.
  hydratedShowcaseId: string | null = null;
  // Per-speaker AI Voice mappings (DE4), cached so re-opening a speaker's "AI
  // Voices" tab doesn't refetch. Written through on every edit; cleared on
  // workspace switch alongside the catalog.
  speakerMappings = new Map<string, SpeakerAiVoiceMapping>();
  // The dub-generate request in flight, if any (PLAT-11570). One at a time:
  // starting another aborts it (the music-separation idiom).
  activeRun: DubRun | null = null;
  // The last run that SUCCEEDED. A failed or cancelled run clears `activeRun`
  // the same way, so this is how a surface tells a new dub from none.
  lastCompletedRun: DubRun | null = null;
  // The separation in flight, if any, and the last one that succeeded — the
  // latter so every mounted separation controller re-reads the stems, not only
  // the one that started the run.
  separationRun: SeparationRun | null = null;
  lastCompletedSeparation: SeparationRun | null = null;

  private pendingLoad: Promise<void> | null = null;
  // Cancel handle for `activeRun`; not observable (a DOM object, nothing reads
  // it reactively).
  private runAbort: AbortController | null = null;
  private separationAbort: AbortController | null = null;
  private logger = LogManager.getLogger('store:DubbingStore');

  constructor(private rootStore: RootStore) {
    // Mappings are always replaced wholesale (setSpeakerMapping), never mutated
    // in place, so keep the map shallow (ref values) per the store convention
    // rather than deep-converting each mapping's array + record.
    makeAutoObservable<this, 'runAbort' | 'separationAbort'>(this, {
      speakerMappings: observable.shallow,
      activeRun: observable.ref,
      lastCompletedRun: observable.ref,
      separationRun: observable.ref,
      lastCompletedSeparation: observable.ref,
      runAbort: false,
      separationAbort: false,
    });
  }

  // Eagerly load the active ai_voice catalog for a workspace. Idempotent per
  // showcase; a different showcaseId clears and re-hydrates (workspace switch).
  async hydrate(showcaseId: string): Promise<void> {
    if (
      this.hydratedShowcaseId === showcaseId &&
      this.loadingStatus === 'loaded'
    ) {
      return;
    }
    if (this.pendingLoad && this.hydratedShowcaseId === showcaseId) {
      return this.pendingLoad;
    }

    runInAction(() => {
      if (this.hydratedShowcaseId !== showcaseId) this.catalog = [];
      this.hydratedShowcaseId = showcaseId;
      this.loadingStatus = 'loading';
    });

    const run = async (): Promise<void> => {
      try {
        const voices =
          await this.rootStore.datoClientStore.aiVoiceRepository.getAllActiveVoices();
        runInAction(() => {
          // Guard against a workspace switch that landed mid-flight.
          if (this.hydratedShowcaseId !== showcaseId) return;
          this.catalog = voices;
          this.loadingStatus = 'loaded';
        });
      } catch {
        runInAction(() => {
          if (this.hydratedShowcaseId !== showcaseId) return;
          this.loadingStatus = 'error';
        });
      } finally {
        this.pendingLoad = null;
      }
    };

    this.pendingLoad = run();
    return this.pendingLoad;
  }

  /**
   * Start a dub-generate request as THE run in flight. Resolves/rejects exactly
   * like `DubbingRepository.generateDub` (an abort rejects with an AbortError —
   * callers tell it apart with `isAbortError`); `activeRun` is set for the whole
   * flight and cleared when it settles, unless a newer run has replaced it.
   * Toasts and analytics stay with the caller: the store only tracks the run.
   */
  async startRun(
    params: StartDubRunParams,
    meta: DubRunMeta,
  ): Promise<DubGenerateResult> {
    this.runAbort?.abort();
    const controller = new AbortController();
    this.runAbort = controller;
    const run: DubRun = {
      storyId: params.storyId,
      targetLanguage: params.targetLanguage,
      languageCaption: meta.languageCaption,
      etaMs: meta.etaMs,
      automatic: meta.automatic,
      startedAt: Date.now(),
    };
    runInAction(() => {
      this.activeRun = run;
    });
    try {
      const result =
        await this.rootStore.datoClientStore.dubbingRepository.generateDub({
          ...params,
          signal: controller.signal,
        });
      runInAction(() => {
        this.lastCompletedRun = run;
      });
      return result;
    } finally {
      runInAction(() => {
        // A newer startRun may already own activeRun — leave it alone.
        if (this.activeRun === run) {
          this.activeRun = null;
          this.runAbort = null;
        }
      });
    }
  }

  /** Abort the run in flight (no-op when idle). The server may still finish
   *  the job; if so the dub shows up on the next dubbed-languages read. */
  cancelRun(): void {
    if (!this.runAbort) return;
    this.logger.info('Dub run cancelled', {
      storyId: this.activeRun?.storyId,
      targetLanguage: this.activeRun?.targetLanguage,
    });
    this.runAbort.abort();
    this.runAbort = null;
    runInAction(() => {
      this.activeRun = null;
    });
  }

  /**
   * Start a voice/music separation as THE separation in flight. Resolves and
   * rejects like `DubbingRepository.separateStems` (an abort rejects with an
   * AbortError); `separationRun` is set for the whole flight. Toasts and
   * analytics stay with the caller, as with `startRun`.
   */
  async startSeparation(
    params: StartSeparationParams,
  ): Promise<StemSeparateResult> {
    this.separationAbort?.abort();
    const controller = new AbortController();
    this.separationAbort = controller;
    const run: SeparationRun = {
      storyId: params.storyId,
      startedAt: Date.now(),
    };
    runInAction(() => {
      this.separationRun = run;
    });
    try {
      const result =
        await this.rootStore.datoClientStore.dubbingRepository.separateStems({
          ...params,
          signal: controller.signal,
        });
      runInAction(() => {
        this.lastCompletedSeparation = run;
      });
      return result;
    } finally {
      runInAction(() => {
        if (this.separationRun === run) {
          this.separationRun = null;
          this.separationAbort = null;
        }
      });
    }
  }

  /** Stop waiting for the separation in flight (no-op when idle). The server
   *  may still finish; the stems then show up on the next summary read. */
  cancelSeparation(): void {
    if (!this.separationAbort) return;
    this.logger.info('Separation cancelled', {
      storyId: this.separationRun?.storyId,
    });
    this.separationAbort.abort();
    this.separationAbort = null;
    runInAction(() => {
      this.separationRun = null;
    });
  }

  clear(): void {
    this.cancelRun();
    this.cancelSeparation();
    runInAction(() => {
      this.catalog = [];
      this.loadingStatus = 'idle';
      this.hydratedShowcaseId = null;
      this.pendingLoad = null;
      this.speakerMappings.clear();
      this.lastCompletedRun = null;
      this.lastCompletedSeparation = null;
    });
  }

  voiceById(id: string): AiVoiceRecord | undefined {
    return this.catalog.find(v => v.id === id);
  }

  // Cached per-speaker AI Voice mapping (undefined until first fetched).
  getSpeakerMapping(speakerId: string): SpeakerAiVoiceMapping | undefined {
    return this.speakerMappings.get(speakerId);
  }

  // Write a speaker's mapping through the cache (after a fetch or an edit).
  setSpeakerMapping(speakerId: string, mapping: SpeakerAiVoiceMapping): void {
    this.speakerMappings.set(speakerId, mapping);
  }
}
